import { useEffect, useMemo, useState } from 'react';
import {
    IconCheck,
    IconCopy,
    IconDownload,
    IconRotateClockwise,
    IconTrash,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { downloadBlob } from '../../toolPageUtils';

function ImageCard({ image, labels, onRotate, onDelete, onCopy, onDownload }) {
    return (
        <div className="group relative overflow-hidden rounded-xl border border-[#ECE8E4] bg-white shadow-[0_4px_12px_rgba(28,24,21,0.06)] dark:border-zinc-700 dark:bg-zinc-900">
            <div className="relative flex aspect-[3/4] items-center justify-center bg-[#F7F5F3] p-3 dark:bg-zinc-800">
                <img
                    src={image.url}
                    alt={image.name}
                    className="max-h-full max-w-full object-contain transition-transform"
                    style={{ transform: `rotate(${image.rotation}deg)` }}
                    draggable={false}
                />

                <div className="pointer-events-none absolute inset-0 bg-[#1C1815]/0 transition-colors group-hover:bg-[#1C1815]/10" />

                <div className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-center gap-2 bg-gradient-to-t from-[#1C1815]/75 to-transparent px-2 pb-3 pt-10 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                        type="button"
                        onClick={onRotate}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 text-[#3D3833] shadow-sm transition-colors hover:bg-white dark:bg-zinc-900/95 dark:text-zinc-200"
                        aria-label={labels.rotate}
                        title={labels.rotate}
                    >
                        <IconRotateClockwise size={20} stroke={1.8} />
                    </button>
                    <button
                        type="button"
                        onClick={onCopy}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 text-[#3D3833] shadow-sm transition-colors hover:bg-white dark:bg-zinc-900/95 dark:text-zinc-200"
                        aria-label={labels.copy}
                        title={labels.copy}
                    >
                        {image.copied ? <IconCheck size={20} stroke={2.1} /> : <IconCopy size={20} stroke={1.8} />}
                    </button>
                    <button
                        type="button"
                        onClick={onDownload}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 text-[#3D3833] shadow-sm transition-colors hover:bg-white dark:bg-zinc-900/95 dark:text-zinc-200"
                        aria-label={labels.download}
                        title={labels.download}
                    >
                        <IconDownload size={20} stroke={1.8} />
                    </button>
                    <button
                        type="button"
                        onClick={onDelete}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 text-[#D33A2C] shadow-sm transition-colors hover:bg-white dark:bg-zinc-900/95"
                        aria-label={labels.delete}
                        title={labels.delete}
                    >
                        <IconTrash size={20} stroke={1.8} />
                    </button>
                </div>
            </div>

            <div className="border-t border-[#ECE8E4] px-3 py-2.5 dark:border-zinc-700">
                <p className="truncate text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">
                    {image.name}
                </p>
            </div>
        </div>
    );
}

export default function ExtractedImagesPreview({
    results,
    activeIndex,
}) {
    const { t } = useTranslation();
    const activeResult = results[activeIndex] ?? results[0] ?? null;
    const [images, setImages] = useState([]);

    useEffect(() => {
        let cancelled = false;
        const next = (activeResult?.images || []).map((image, index) => ({
            id: `${image.name}-${index}`,
            name: image.name,
            blob: image.blob,
            url: URL.createObjectURL(image.blob),
            rotation: 0,
            copied: false,
        }));
        if (!cancelled) setImages(next);

        return () => {
            cancelled = true;
            next.forEach((image) => URL.revokeObjectURL(image.url));
        };
    }, [activeResult]);

    const labels = useMemo(() => ({
        rotate: t('extractImagesPdf.imageActions.rotate'),
        copy: t('extractImagesPdf.imageActions.copy'),
        delete: t('extractImagesPdf.imageActions.delete'),
        download: t('extractImagesPdf.download'),
    }), [t]);

    const handleRotate = (id) => {
        setImages((prev) => prev.map((image) => (
            image.id === id ? { ...image, rotation: (image.rotation + 90) % 360 } : image
        )));
    };

    const handleDelete = (id) => {
        setImages((prev) => prev.filter((image) => image.id !== id));
    };

    const handleCopy = async (id) => {
        const image = images.find((item) => item.id === id);
        if (!image) return;

        try {
            if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
                await navigator.clipboard.write([new ClipboardItem({ [image.blob.type]: image.blob })]);
                setImages((prev) => prev.map((item) => (item.id === id ? { ...item, copied: true } : item)));
                setTimeout(() => {
                    setImages((prev) => prev.map((item) => (item.id === id ? { ...item, copied: false } : item)));
                }, 1400);
                notifications.show({
                    title: t('common.success'),
                    message: t('extractImagesPdf.imageActions.copied'),
                    color: 'green',
                });
            }
        } catch {
            notifications.show({
                title: t('common.error'),
                message: t('extractImagesPdf.imageActions.copyFailed'),
                color: 'red',
            });
        }
    };

    if (!activeResult) return null;

    return (
        <div className="h-full overflow-auto px-1 py-1 [scrollbar-width:thin]">
            <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-[#ECE8E4] bg-white px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900">
                <p className="truncate text-sm font-semibold text-[#3D3833] dark:text-zinc-100">
                    {activeResult.name}
                </p>
                <span className="flex-none rounded-full bg-pdf-cat-tint px-2.5 py-1 text-[11px] font-semibold text-pdf-cat-solid dark:bg-orange-950/30 dark:text-orange-400">
                    {t('extractImagesPdf.imageCount', { count: toBnDigits(images.length) })}
                </span>
            </div>
            <div className={`grid gap-4 ${images.length === 1 ? 'mx-auto max-w-sm grid-cols-1' : 'grid-cols-3'}`}>
                {images.map((image) => (
                    <ImageCard
                        key={image.id}
                        image={image}
                        labels={labels}
                        onRotate={() => handleRotate(image.id)}
                        onDelete={() => handleDelete(image.id)}
                        onCopy={() => handleCopy(image.id)}
                        onDownload={() => downloadBlob(image.blob, image.name)}
                    />
                ))}
            </div>
        </div>
    );
}
